/**
 * Cross-surface information-density contract.
 *
 * Browser and native renderers deliberately remain platform-specific, but
 * they resolve first-paint prominence from this one policy. Work that needs a
 * person now is never allowed behind progressive disclosure.
 */
export const CONTENT_DENSITY_LEVELS = ['actionable', 'supporting', 'historical'] as const;
export type ContentDensityLevel = (typeof CONTENT_DENSITY_LEVELS)[number];

export const CONTENT_URGENCY_LEVELS = ['P0', 'P1', 'P2', 'P3'] as const;
export type ContentUrgencyLevel = (typeof CONTENT_URGENCY_LEVELS)[number];

export interface ContentDensityInput {
  level: ContentDensityLevel;
  urgency?: ContentUrgencyLevel | null;
  requiresAction: boolean;
}

export interface ContentDensityPresentation {
  presentation: 'always_visible' | 'progressive_disclosure';
  defaultExpanded: boolean;
  canCollapse: boolean;
  reason: 'action_required' | 'urgent_work' | 'actionable_level' | 'supporting_or_historical';
}

export const CONTENT_DENSITY_POLICY = {
  version: '1.0.0-candidate.1',
  actionablePresentation: 'always_visible',
  supportingPresentation: 'progressive_disclosure',
  urgentLevels: ['P0', 'P1'],
  crossSurfaceAdapters: {
    browser: '@statura/ui/ContentDensitySection',
    native: 'resolveNativeContentDensityPresentation',
  },
} as const;

export function resolveContentDensityPresentation(
  input: ContentDensityInput,
): ContentDensityPresentation {
  if (input.requiresAction) {
    return {
      presentation: 'always_visible',
      defaultExpanded: true,
      canCollapse: false,
      reason: 'action_required',
    };
  }

  if (input.urgency === 'P0' || input.urgency === 'P1') {
    return {
      presentation: 'always_visible',
      defaultExpanded: true,
      canCollapse: false,
      reason: 'urgent_work',
    };
  }

  if (input.level === 'actionable') {
    return {
      presentation: 'always_visible',
      defaultExpanded: true,
      canCollapse: false,
      reason: 'actionable_level',
    };
  }

  return {
    presentation: 'progressive_disclosure',
    defaultExpanded: false,
    canCollapse: true,
    reason: 'supporting_or_historical',
  };
}
